/**
 * CR107: Magical UX Layer - Intelligent Hover Hints
 * 
 * Provides contextual tooltips that fade out as users learn the system
 */

import { useState, useEffect, ReactNode } from "react";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { useMagicalUX } from "./MagicalUXProvider";

interface HoverHintProps {
  hintId: string;
  content: string;
  children: ReactNode;
  side?: "top" | "bottom" | "left" | "right";
  delay?: number;
}

const HINT_MESSAGES = {
  'layers-panel': 'Layers help you organize your layout components.',
  'inspector-panel': 'Edit styles and layout settings here.',
  'canvas-area': 'Drop components here to build your page.',
  'component-library': 'Choose from pre-built components to add to your page.',
  'preview-mode': 'See how your page looks to visitors.',
  'export-button': 'Export your layout configuration when ready.',
  'undo-redo': 'Undo/redo your recent changes.',
  'zoom-controls': 'Zoom in/out to see more detail or get an overview.',
};

export function HoverHint({ hintId, content, children, side = "bottom", delay = 500 }: HoverHintProps) {
  const { shouldShowHint, markStepComplete } = useMagicalUX();
  const [hasShown, setHasShown] = useState(false);
  const [isOpen, setIsOpen] = useState(false);

  const shouldDisplay = shouldShowHint(hintId) && !hasShown;

  const handleOpenChange = (open: boolean) => {
    setIsOpen(open);
    if (open && !hasShown) {
      setHasShown(true);
      // Mark as seen after showing for 3 seconds
      setTimeout(() => {
        markStepComplete(`hint-${hintId}`);
      }, 3000);
    }
  };

  if (!shouldDisplay) {
    return <>{children}</>;
  }

  return (
    <TooltipProvider>
      <Tooltip open={isOpen} onOpenChange={handleOpenChange} delayDuration={delay}>
        <TooltipTrigger asChild>
          {children}
        </TooltipTrigger>
        <TooltipContent 
          side={side}
          className="bg-background border-border text-foreground animate-fade-in"
          sideOffset={8}
        >
          <div className="flex items-center gap-2">
            <span className="text-muted-foreground">💡</span>
            <span className="text-xs font-medium">{content}</span>
          </div>
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}

// Pre-configured common hints
export const LayersPanelHint = ({ children }: { children: ReactNode }) => (
  <HoverHint hintId="layers-panel" content={HINT_MESSAGES['layers-panel']}>
    {children}
  </HoverHint>
);

export const InspectorPanelHint = ({ children }: { children: ReactNode }) => (
  <HoverHint hintId="inspector-panel" content={HINT_MESSAGES['inspector-panel']}>
    {children}
  </HoverHint>
);

export const CanvasAreaHint = ({ children }: { children: ReactNode }) => (
  <HoverHint hintId="canvas-area" content={HINT_MESSAGES['canvas-area']}>
    {children}
  </HoverHint>
);

export const ComponentLibraryHint = ({ children }: { children: ReactNode }) => (
  <HoverHint hintId="component-library" content={HINT_MESSAGES['component-library']}>
    {children}
  </HoverHint>
);